/**
 * Per-quote write chain.
 *
 * The backend rejects concurrent writes to the same quote with 409, and two
 * rule evaluations running at once on one quote can each insert the same
 * conditional term. Every request that writes to an existing quote therefore
 * runs through this chain, keyed by quoteId: writes to one quote go out one at
 * a time in the order they were requested, while writes to different quotes
 * stay parallel. Module scope ensures every consumer shares one chain,
 * including separate hook instances for the same quote.
 *
 * Who enqueues:
 * - `use*` mutation hooks under `api/` that write to a quote enqueue inside
 *   their `mutationFn`, so `mutate`/`isPending` cover the wait as well as the
 *   request and no call site can forget. React Query runs `onMutate` before
 *   `mutationFn`, so a hook reserves its slot a few microtasks after `mutate()`
 *   is called: writes never overlap, but a chained action requested
 *   synchronously in the same tick may run first.
 * - Plain `do*` request functions never enqueue; their callers do (see
 *   `useQuote`, `useQuoteOffering`, `AttachedQuoteDocuments`).
 *
 * Never await a chained call from inside an `enqueueForQuote` body. The chain
 * is shared, so the inner call would wait on the outer one and deadlock.
 */
const quoteWriteChains = new Map<string, Promise<unknown>>();

export function enqueueForQuote<T>(
  quoteId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const prev = quoteWriteChains.get(quoteId) ?? Promise.resolve();
  const next = prev.catch(() => undefined).then(fn);
  quoteWriteChains.set(quoteId, next);
  // Cleanup branch must absorb rejection so it isn't reported as unhandled —
  // callers receive their own reference to `next` and handle (or rethrow) it.
  next
    .catch(() => undefined)
    .finally(() => {
      if (quoteWriteChains.get(quoteId) === next) {
        quoteWriteChains.delete(quoteId);
      }
    });
  return next;
}
